// App 連線狀態（spec 10 §4.3「App 連線狀態機（單一真值）」的 web 落地 + §7
// 離線狀態 UI、AC-OS-8）。
//
// Web v1 取三態子集（六態中 cold-start 輕提示 / session-expired / force-logout
// 各有既有 owner——啟動流程與 AuthContext）：
//   online   = network up + WSS open（或尚未需要 WSS）→ 無 banner
//   degraded = network up（REST 視為可用）+ WSS 斷線重連中 → 黃 banner
//              「雲端連線中斷，重新連線中…」+ 即時功能入口 disable
//   offline  = network down（navigator online/offline 事件）→ 灰 banner
//              （web v1 無 outbox → 不顯計數，僅狀態提示）
//
// 判定的 WSS 來源是 ws.ts 既有狀態機：'reconnecting' 即「曾嘗試過連線且目前
// 斷線/重試中」（含 signin_error 後的重試迴圈）。首次 'connecting' 屬
// cold-start 範疇、'idle'/'closed'（未連線/使用者登出）不是降級——僅
// 'reconnecting' 構成 degraded，避免登入瞬間黃 banner 閃爍。

import { useEffect, useState } from 'react';
import i18n from '../i18n';
import { signaling, type SignalingState } from './ws';

export type ConnectionMode = 'online' | 'degraded' | 'offline';

/**
 * spec 10 §4.3/§7 逐字文案（e2e 以字串斷言，不得改寫）。
 * ⚠️ 既有匯入點（DevicesPage / LiveHubPage / DeviceDetailPage / ConnectionBanner +
 * connection.test）以 named import 取 DEGRADED_BANNER_TEXT / OFFLINE_BANNER_TEXT 字串，
 * 不可改成 function（會破壞那些不在本批次範圍內的檔）。故維持 const 字串，於模組載入時
 * 由 i18n 求值。⚠️ 限制：模組載入後切換語言不會更新此兩字串（rule #5），但初始語言正確；
 * 完整 reactive 待相關頁面一併改為 useTranslation 時再處理。zh-Hant 與 spec 逐字一致。
 */
export const DEGRADED_BANNER_TEXT: string = i18n.t('shell.connection.degraded');
export const OFFLINE_BANNER_TEXT: string = i18n.t('shell.connection.offline');

/** 純函數版判定（單元測試直驅）。 */
export function connectionMode(ws: SignalingState, browserOnline: boolean): ConnectionMode {
  if (!browserOnline) return 'offline';
  if (ws === 'reconnecting') return 'degraded';
  return 'online';
}

/**
 * 訂閱全域連線狀態（signaling 單例 state + window online/offline）。
 * AppShell banner 與各「即時功能入口」（live 觀看鈕）共用此 hook
 * （spec 10 §4.3：degraded → 即時功能 disable，REST 讀寫照常）。
 */
export function useConnectionMode(): ConnectionMode {
  const [wsState, setWsState] = useState<SignalingState>(() => signaling.getState());
  const [online, setOnline] = useState<boolean>(() => navigator.onLine !== false);

  useEffect(() => {
    const off = signaling.on('state', setWsState);
    // 掛載與訂閱之間的空窗：補讀一次當前值
    setWsState(signaling.getState());
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      off();
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  return connectionMode(wsState, online);
}
